import { money } from "../lib/store";
import { billingTotals, valuationTotals, monthlyDraw, confirmedPaid, claimedPaid, isConfirmed } from "../lib/billing";
import { fmtDate, daysUntil } from "../lib/format";
import type { Project, Invoice } from "../lib/types";

/* Every receipt, traced to the invoice and the stage it paid for, with a running
   balance — the view an accountant needs to reconcile, and the one a client
   needs to see that their money landed where they think it did.
   Read-only: recording a payment happens against its invoice, on Billing. */

export interface LedgerRow {
  on: string;
  invoiceRef: string;
  what: string;
  method?: string;
  reference?: string;
  amount: number;
  balanceAfter: number;
}

/** Receipts oldest-first with the outstanding balance after each one.
 *  Invoices settled before receipts were kept still belong in the trail, so a
 *  paid invoice with no recorded payment contributes one for its full amount. */
export function ledgerRows(p: Project): LedgerRow[] {
  const rows: LedgerRow[] = p.invoices.flatMap((inv) => {
    const pays = (inv.payments ?? []).filter(isConfirmed);
    if (pays.length === 0 && inv.status === "paid") {
      return [{ on: inv.paidOn ?? inv.dueOn, invoiceRef: inv.ref, what: inv.title, amount: inv.amount, balanceAfter: 0 }];
    }
    return pays.map((pay) => ({
      on: pay.on, invoiceRef: inv.ref, what: inv.title,
      method: pay.method, reference: pay.reference, amount: pay.amount, balanceAfter: 0,
    }));
  }).sort((a, b) => (a.on < b.on ? -1 : a.on > b.on ? 1 : 0));

  const invoiced = p.invoices.reduce((s, i) => s + i.amount, 0);
  let paid = 0;
  return rows.map((r) => { paid += r.amount; return { ...r, balanceAfter: Math.max(0, invoiced - paid) }; });
}

export interface OpenInvoice { inv: Invoice; paid: number; owed: number; claimed: number }

/** Invoices issued and not yet settled — the only money actually payable. */
export function openInvoices(p: Project): OpenInvoice[] {
  return p.invoices
    .filter((i) => i.status !== "paid" && i.status !== "scheduled")
    .map((inv) => {
      const paid = confirmedPaid(inv);
      return { inv, paid, owed: Math.max(0, inv.amount - paid), claimed: claimedPaid(inv) };
    })
    .filter((x) => x.owed > 0);
}

export interface ClaimRow { invoiceId: string; invoiceRef: string; what: string; pay: NonNullable<Project["invoices"][number]["payments"]>[number] }

/** What clients say they have sent, still waiting on the studio to confirm. */
export function claimRows(p: Project): ClaimRow[] {
  return p.invoices.flatMap((inv) =>
    (inv.payments ?? []).filter((x) => !isConfirmed(x))
      .map((pay) => ({ invoiceId: inv.id, invoiceRef: inv.ref, what: inv.title, pay })));
}

export interface Upcoming { label: string; detail: string; amount: number; when?: string }

/** What is expected next, and roughly when — never presented as owed. */
export function upcomingRows(p: Project): Upcoming[] {
  const b = p.billing;
  if (!b) return [];
  const out: Upcoming[] = [];

  for (const v of b.valuations ?? []) {
    if (v.monthly) {
      const d = monthlyDraw(v);
      if (d && d.remainingMonths > 0 && d.balance > 0) {
        out.push({
          label: `${v.stage} — next monthly draw`,
          detail: `${d.remainingMonths} month${d.remainingMonths === 1 ? "" : "s"} to run · ${money(d.balance, b.currency)} left on the stage`,
          amount: d.net,
        });
      }
    } else if (!v.invoiceId) {
      const t = valuationTotals(v);
      if (t.total > 0) {
        out.push({ label: v.stage, detail: `Certified on completion${t.deferred ? ` · ${money(t.deferred, b.currency)} of it deferred` : ""}`, amount: t.total });
      }
    }
  }

  if (b.billingMode !== "valuation") {
    for (const m of b.milestones.filter((x) => x.status === "scheduled" && x.amount > 0)) {
      out.push({ label: m.label, detail: m.basis ?? "On the agreed trigger", amount: m.amount, when: m.dueOn });
    }
  }

  const t = billingTotals(p);
  if (b.deferred && t && t.deferred > 0) {
    out.push({ label: "Deferred amount", detail: `Held back as earned — payable ${b.deferred.trigger}`, amount: t.deferred });
  }
  return out;
}

const CELL = { padding: "9px 12px", whiteSpace: "nowrap" as const };
const TH = { ...CELL, borderBottom: "1px solid var(--line)", color: "var(--ink-3)", fontSize: 11, textTransform: "uppercase" as const, letterSpacing: ".04em", fontWeight: 500 };

/* ------------------------------------------------------------- receipts */

export function ReceiptsCard({ p, mode }: { p: Project; mode: "studio" | "client" }) {
  const cur = p.billing?.currency ?? p.currency;
  const rows = ledgerRows(p);
  return (
    <div className="card reveal" style={{ overflow: "hidden" }}>
      <div className="row between" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)" }}>
        <span className="kicker">{mode === "client" ? "Payments you have made" : "Payments received"}</span>
        <span className="dim" style={{ fontSize: 12 }}>
          {rows.length} receipt{rows.length === 1 ? "" : "s"} · {money(rows.reduce((s, r) => s + r.amount, 0), cur)}
        </span>
      </div>
      {rows.length === 0
        ? <div className="dim" style={{ padding: 18, fontSize: 13 }}>No payments recorded yet.</div>
        : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 620 }}>
              <thead>
                <tr>{["Date", "Against", "Method", "Reference", "Amount", "Balance after"].map((h, i) => (
                  <th key={h} style={{ ...TH, textAlign: i >= 4 ? "right" : "left" }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: i < rows.length - 1 ? "1px solid var(--line)" : "none" }}>
                    <td style={CELL}>{fmtDate(r.on)}</td>
                    <td style={{ ...CELL, whiteSpace: "normal" }}>
                      {r.what}<span className="dim mono" style={{ fontSize: 11 }}> · {r.invoiceRef}</span>
                    </td>
                    <td style={CELL} className="dim">{r.method ?? "—"}</td>
                    <td style={CELL} className="dim mono">{r.reference ?? "—"}</td>
                    <td style={{ ...CELL, textAlign: "right", color: "var(--ok)" }} className="mono">{money(r.amount, cur)}</td>
                    <td style={{ ...CELL, textAlign: "right" }} className="mono">{money(r.balanceAfter, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

/* ------------------------------------------------------------- upcoming */

export function UpcomingCard({ p }: { p: Project }) {
  const cur = p.billing?.currency ?? p.currency;
  const upcoming = upcomingRows(p);
  if (upcoming.length === 0) return null;
  return (
    <div className="card reveal" style={{ overflow: "hidden" }}>
      <div className="row between" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)" }}>
        <span className="kicker">Upcoming payments</span>
        <span className="dim" style={{ fontSize: 12 }}>{money(upcoming.reduce((s, u) => s + u.amount, 0), cur)} expected</span>
      </div>
      <div className="dim" style={{ padding: "10px 18px 2px", fontSize: 11.5 }}>
        Expected, not yet invoiced — nothing here is payable until it has been billed.
      </div>
      {upcoming.map((u, i) => (
        <div key={i} className="row between wrap" style={{ padding: "11px 18px", borderTop: "1px solid var(--line)", gap: 10 }}>
          <span style={{ minWidth: 0 }}>
            <span style={{ fontSize: 13.5 }}>{u.label}</span>
            <span className="dim" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>{u.detail}</span>
          </span>
          <span className="row" style={{ gap: 8, flexShrink: 0 }}>
            {u.when && <span className="dim" style={{ fontSize: 11 }}>{fmtDate(u.when)}</span>}
            <span className="mono" style={{ fontSize: 13 }}>{money(u.amount, cur)}</span>
          </span>
        </div>
      ))}
    </div>
  );
}
